import React from 'react'
import { PageShell } from './PageShell'
import { NudgeStatusBadge } from '../components/NudgeStatusBadge'
import { ConversationThread } from '../components/ConversationThread'
import { useDashboard } from '../DashboardContext'
import { formatDateTime, formatDateShort, initialsFromName, dateOnly } from '../utils'
import { triggerDevScanner } from '../../../lib/nudgeApi'
import { SkeletonTableRows } from '../../../components/ui/Skeleton'
import type { NudgeQueueSummary } from '../../../types/api'
import type { NudgeStatus } from '../../../types/enums'

type StatusGroup = 'all' | 'in-progress' | 'sent' | 'replied' | 'recovered' | 'closed'

const IN_PROGRESS: NudgeStatus[] = ['PENDING', 'AI_PROCESSING', 'AI_READY', 'SENDING']
const REPLIED: NudgeStatus[] = ['REPLY_RECEIVED', 'CONV_ACTIVE']
const CLOSED: NudgeStatus[] = ['OPT_OUT', 'EXHAUSTED', 'FAILED', 'RETURNED', 'NO_SHOW']

function matchesGroup(status: NudgeStatus, group: StatusGroup): boolean {
  if (group === 'all') return true
  if (group === 'in-progress') return IN_PROGRESS.includes(status)
  if (group === 'sent') return status === 'SENT'
  if (group === 'replied') return REPLIED.includes(status)
  if (group === 'recovered') return status === 'RECOVERED'
  if (group === 'closed') return CLOSED.includes(status)
  return true
}

type SortKey = 'created-desc' | 'name-asc' | 'sent-desc'

type ContactGroup = {
  contactId: string
  contactName: string
  contactEmail: string
  nudges: NudgeQueueSummary[]  // sorted ASC by sequenceNumber
  latest: NudgeQueueSummary
  latestSentAt: string | null
  latestFollowUp: string | null
  latestReplyAt: string | null
}

function groupByContact(rows: NudgeQueueSummary[]): ContactGroup[] {
  const map = new Map<string, NudgeQueueSummary[]>()
  for (const row of rows) {
    const existing = map.get(row.contactId) ?? []
    existing.push(row)
    map.set(row.contactId, existing)
  }
  // Pick the most recent value across all rows by timestamp.
  const mostRecent = (vals: (string | null | undefined)[]): string | null => {
    const filtered = vals.filter((v): v is string => Boolean(v))
    if (filtered.length === 0) return null
    return filtered.reduce((a, b) =>
      new Date(a).getTime() >= new Date(b).getTime() ? a : b
    )
  }

  return Array.from(map.values()).map(nudges => {
    // Display order: by sequenceNumber ASC (consumed by the campaign-grouping renderer)
    const sorted = [...nudges].sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    // Most-recent-first ordering, for picking the row whose status drives the badge
    const byRecent = [...nudges].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    // Prefer RECOVERED (needs owner action) over RETURNED (done), else the chronologically latest row
    const recoveredRow = byRecent.find(n => n.status === 'RECOVERED')
      ?? byRecent.find(n => n.status === 'RETURNED')
    const latest = recoveredRow ?? byRecent[0]
    return {
      contactId: latest.contactId,
      contactName: latest.contactName,
      contactEmail: latest.contactEmail,
      nudges: sorted,
      latest,
      latestSentAt: mostRecent(nudges.map(n => n.sentAt)),
      latestFollowUp: mostRecent(nudges.map(n => n.followUpSendAfter)),
      latestReplyAt: mostRecent(nudges.map(n => n.replyReceivedAt)),
    }
  })
}

// Split a contact's nudges into separate campaigns, ordered chronologically.
// A new campaign starts whenever sequenceNumber resets to 1 (LapseScannerJob
// enqueues a fresh attempt after the previous one terminated as NO_SHOW,
// EXHAUSTED, OPT_OUT, etc.).
function groupIntoCampaigns(nudges: NudgeQueueSummary[]): NudgeQueueSummary[][] {
  const chronological = [...nudges].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )
  const campaigns: NudgeQueueSummary[][] = []
  let current: NudgeQueueSummary[] = []
  for (const n of chronological) {
    if (n.sequenceNumber === 1 && current.length > 0) {
      campaigns.push(current)
      current = []
    }
    current.push(n)
  }
  if (current.length > 0) campaigns.push(current)
  return campaigns
}

function sortGroups(groups: ContactGroup[], sort: SortKey): ContactGroup[] {
  return [...groups].sort((a, b) => {
    if (sort === 'name-asc') return a.contactName.localeCompare(b.contactName)
    if (sort === 'sent-desc') {
      const at = (g: ContactGroup) => g.latestSentAt ? new Date(g.latestSentAt).getTime() : 0
      return at(b) - at(a)
    }
    const latestCreated = (g: ContactGroup) =>
      Math.max(...g.nudges.map(n => new Date(n.createdAt).getTime()))
    return latestCreated(b) - latestCreated(a)
  })
}

const PAGE_SIZE = 50

export function LapsedPage({ isActive }: { isActive: boolean }) {
  const { businessInfo, queueRows, queueLoading, refreshQueue } = useDashboard()

  const [page, setPage] = React.useState(0)
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<StatusGroup>('all')
  const [sort, setSort] = React.useState<SortKey>('created-desc')
  const [nudgeDateFilter, setNudgeDateFilter] = React.useState(() => new Date().toISOString().slice(0, 10))

  const [selectedGroup, setSelectedGroup] = React.useState<ContactGroup | null>(null)
  const [showActivityLog, setShowActivityLog] = React.useState(false)
  const [activityLogDate, setActivityLogDate] = React.useState('')
  const [scannerRunning, setScannerRunning] = React.useState(false)
  async function handleTriggerScanner() {
    setScannerRunning(true)
    try {
      await triggerDevScanner()
      refreshQueue()
    } finally {
      setScannerRunning(false)
    }
  }

  const allGroups = React.useMemo(() => groupByContact(queueRows), [queueRows])

  const allFiltered = React.useMemo(() => {
    let result = allGroups.filter(g => matchesGroup(g.latest.status, statusFilter))

    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter(g =>
        `${g.contactName} ${g.contactEmail}`.toLowerCase().includes(q)
      )
    }

    if (nudgeDateFilter) {
      result = result.filter(g =>
        g.nudges.some(n => {
          const ts = n.sentAt ?? n.followUpSendAfter ?? n.createdAt
          return dateOnly(ts) === nudgeDateFilter
        })
      )
    }

    return sortGroups(result, sort)
  }, [allGroups, statusFilter, search, nudgeDateFilter, sort])

  const totalElements = allFiltered.length
  const totalPages = Math.ceil(totalElements / PAGE_SIZE)
  const filteredGroups = allFiltered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  React.useEffect(() => { setPage(0) }, [search, statusFilter, nudgeDateFilter, sort])

  const lapseDays = businessInfo?.lapseWindowDays ?? 60

  const inProgressCount = allGroups.filter(g =>
    g.nudges.some(n => IN_PROGRESS.includes(n.status))
  ).length

  // "IN QUEUE" = contacts that haven't successfully come back yet (exclude RETURNED only)
  const activeQueueCount = allGroups.filter(g => g.latest.status !== 'RETURNED').length

  const activityLogRows = queueRows.filter(r => {
    const hasActivity = r.sentAt || r.replyReceivedAt
    if (!hasActivity) return false
    if (!activityLogDate) return true
    const ts = r.replyReceivedAt ?? r.sentAt ?? ''
    return dateOnly(ts) === activityLogDate
  })

  return (
    <>
      <PageShell id="page-lapsed" isActive={isActive}>
        <div className="content">
          <div className="lapsed-header-bar">
            <div>
              <div className="lh-num">{activeQueueCount}</div>
              <div
                style={{
                  fontSize: 10,
                  fontFamily: "'DM Mono',monospace",
                  color: 'var(--text3)',
                  marginTop: 2,
                }}
              >
                IN QUEUE
              </div>
            </div>
            <div className="lh-divider" />
            <div>
              <div className="lh-label">Haven't visited in {lapseDays}+ days</div>
              <div className="lh-sub">
                Auto scan runs daily at {businessInfo?.dailyScanTime || '9:00 AM'} ·{' '}
                {inProgressCount > 0
                  ? `${inProgressCount} nudge${inProgressCount === 1 ? '' : 's'} in progress`
                  : 'All nudges sent'}
              </div>
            </div>
            <div className="lh-divider" />
            <div>
              <div className="lh-label">Lapse window</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 300, color: 'var(--text)' }}>
                  {lapseDays}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text2)',
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  days
                </span>
                <button className="action-btn" style={{ marginLeft: 8 }}>
                  Change
                </button>
              </div>
            </div>
          </div>

          <div className="filter-row">
            <div className="search-bar">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                placeholder="Search by name or email…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0) }}
                aria-label="Search nudge queue by name or email"
              />
            </div>
            <select
              className="select-input"
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value as StatusGroup)
                setPage(0)
              }}
              aria-label="Filter by nudge status"
            >
              <option value="all">All statuses</option>
              <option value="in-progress">In Progress</option>
              <option value="sent">Sent — Awaiting Reply</option>
              <option value="replied">Replied</option>
              <option value="recovered">Recovered</option>
              <option value="closed">Closed</option>
            </select>
            <select
              className="select-input"
              value={sort}
              onChange={e => setSort(e.target.value as SortKey)}
              aria-label="Sort nudge queue"
            >
              <option value="created-desc">Sort: Newest first</option>
              <option value="name-asc">Sort: Name A–Z</option>
              <option value="sent-desc">Sort: Sent date</option>
            </select>
            <input
              type="date"
              className="select-input"
              value={nudgeDateFilter}
              onChange={e => { setNudgeDateFilter(e.target.value); setPage(0) }}
              title="Filter by nudge date"
            />
            {nudgeDateFilter ? (
              <button type="button" className="btn-secondary" onClick={() => setNudgeDateFilter('')}>
                Clear date
              </button>
            ) : null}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowActivityLog(true)}
            >
              Nudge Activity Log
            </button>
            {import.meta.env.DEV || import.meta.env.MODE === 'staging' ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={scannerRunning}
                onClick={handleTriggerScanner}
                title="Manually runs the lapse scanner for this business (dev/staging only)"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}
              >
                {scannerRunning ? 'Running…' : 'Test Scheduler'}
              </button>
            ) : null}
          </div>

          <div className="panel" aria-busy={queueLoading} aria-label="Nudge queue">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Nudge #</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th>Next Follow-up</th>
                  <th>Reply At</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {queueLoading ? (
                  <SkeletonTableRows cols={7} rows={5} />
                ) : filteredGroups.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 8,
                          padding: '36px 16px',
                          color: 'var(--text2)',
                          fontSize: 13,
                        }}
                      >
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ opacity: 0.35 }}
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>
                          {allGroups.length === 0
                            ? 'No nudges in the queue yet. Your first scan will run at your scheduled time.'
                            : 'No nudges match the current filters.'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredGroups.map(group => (
                    <tr key={group.contactId}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="avatar-circle"
                            style={{ background: 'var(--orange-bg)', color: 'var(--orange)' }}
                          >
                            {initialsFromName(group.contactName)}
                          </div>
                          <div>
                            <div className="cust-name">{group.contactName}</div>
                            <div className="cust-sub">{group.contactEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const campaigns = groupIntoCampaigns(group.nudges)
                          if (campaigns.length === 1) {
                            return (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {campaigns[0].map(n => (
                                  <span key={n.id} className="pill pill-pending">
                                    Nudge #{n.sequenceNumber}
                                  </span>
                                ))}
                              </div>
                            )
                          }
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {campaigns.map((camp, i) => (
                                <div
                                  key={i}
                                  style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                                >
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: 'var(--text3)',
                                      fontFamily: "'DM Mono',monospace",
                                      fontWeight: 600,
                                      letterSpacing: '0.04em',
                                    }}
                                  >
                                    Attempt {i + 1}:
                                  </span>
                                  {camp.map(n => (
                                    <span key={n.id} className="pill pill-pending">
                                      Nudge #{n.sequenceNumber}
                                    </span>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <NudgeStatusBadge status={group.latest.status} />
                          {group.latest.status === 'RECOVERED' && group.latest.committedByDate && (
                            <span
                              style={{
                                fontSize: 11,
                                color: 'var(--text3)',
                                fontFamily: "'DM Mono',monospace",
                              }}
                              title="Date the customer said they would return by"
                            >
                              Expected {formatDateShort(group.latest.committedByDate)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {group.latestSentAt ? formatDateShort(group.latestSentAt) : '—'}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {group.latestFollowUp ? formatDateShort(group.latestFollowUp) : '—'}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {group.latestReplyAt ? formatDateShort(group.latestReplyAt) : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            className="action-btn"
                            type="button"
                            onClick={() => setSelectedGroup(group)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginTop: 16,
              }}
            >
              <button
                className="btn-secondary"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </button>
              <span
                style={{
                  fontSize: 12,
                  fontFamily: "'DM Mono',monospace",
                  color: 'var(--text2)',
                }}
              >
                Page {page + 1} of {totalPages}
              </span>
              <button
                className="btn-secondary"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
          ) : null}
        </div>
      </PageShell>

      {selectedGroup ? (
        <ConversationThread
          summaries={selectedGroup.nudges}
          onClose={() => setSelectedGroup(null)}
        />
      ) : null}

      {showActivityLog ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 95,
            display: 'grid',
            placeItems: 'center',
            padding: 24,
          }}
          onClick={() => setShowActivityLog(false)}
        >
          <div
            className="panel"
            style={{
              width: 'min(920px, 96vw)',
              padding: 18,
              maxHeight: '86vh',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: 24 }}>
                Nudge Activity Log
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowActivityLog(false)}
              >
                Close
              </button>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div style={{ color: 'var(--text2)' }}>Nudges sent and replies received.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)' }}>Date</label>
                <input
                  type="date"
                  className="select-input"
                  value={activityLogDate}
                  onChange={e => setActivityLogDate(e.target.value)}
                />
                {activityLogDate ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setActivityLogDate('')}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
            <div className="panel">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Nudge #</th>
                    <th>Status</th>
                    <th>Sent At</th>
                    <th>Reply At</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogRows.map(row => (
                    <tr key={row.id}>
                      <td>{row.contactName}</td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {row.contactEmail}
                      </td>
                      <td>
                        <span className="pill pill-pending" data-tooltip="Outreach sequence number">
                          Nudge #{row.sequenceNumber}
                        </span>
                      </td>
                      <td>
                        <NudgeStatusBadge status={row.status} />
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {row.sentAt ? formatDateTime(row.sentAt) : '—'}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {row.replyReceivedAt ? formatDateTime(row.replyReceivedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                  {activityLogRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: 'center',
                          padding: '22px 12px',
                          color: 'var(--text2)',
                          fontSize: 13,
                        }}
                      >
                        No activity found{activityLogDate ? ` for ${activityLogDate}` : ''}.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
